import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Activity, ArrowRight, TrendingUp, TrendingDown } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const pub = axios.create({ baseURL: API });
const GRID = "hsl(240 4% 16%)";
const ACCENT = "hsl(221 83% 53%)";
const BARS = ["hsl(221 83% 53%)", "hsl(142 71% 45%)", "hsl(45 93% 47%)", "hsl(280 65% 60%)", "hsl(190 90% 50%)"];
const tooltipStyle = { background: "hsl(240 6% 8%)", border: "1px solid hsl(240 4% 16%)", borderRadius: 6, fontSize: 12, fontFamily: "JetBrains Mono" };
const MARGIN = { top: 5, right: 8, left: -6, bottom: 0 };
const TICK = { fontSize: 10, fill: "hsl(240 5% 65%)" };
const kfmt = (v) => {
  if (v == null) return v;
  const a = Math.abs(v);
  if (a >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (a >= 1000) return `${Math.round(v / 1000)}k`;
  return `${v}`;
};

function fmt(v, f, unit) {
  if (v == null) return "—";
  if (f === "currency") return "$" + Math.round(v).toLocaleString();
  if (f === "percent") return v.toFixed(1) + "%";
  if (Math.abs(v) >= 1000) return Math.round(v).toLocaleString();
  return v.toFixed(f === "number" ? 0 : 1) + (unit && unit !== "$" ? ` ${unit}` : "");
}

function KpiCard({ m }) {
  const up = (m.period_change ?? 0) >= 0;
  const good = m.higher_better ? up : !up;
  return (
    <div className="rounded-xl border border-border bg-card/60 p-4 backdrop-blur" data-testid={`demo-kpi-${m.key}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{m.label}</span>
        <span className={`flex items-center gap-1 text-xs font-mono ${good ? "text-[hsl(142_71%_55%)]" : "text-[hsl(0_72%_60%)]"}`}>
          {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {Math.abs(m.period_change ?? 0).toFixed(1)}%
        </span>
      </div>
      <div className="mt-1 text-2xl font-heading font-semibold tabular-nums">{fmt(m.value, m.format, m.unit)}</div>
      <div className="mt-2 h-8">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={(m.sparkline || []).map((v, i) => ({ i, v }))}>
            <Line type="monotone" dataKey="v" stroke={good ? "hsl(142 71% 45%)" : "hsl(0 72% 55%)"} strokeWidth={1.5} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Demo() {
  const [summary, setSummary] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [users, setUsers] = useState([]);
  const [breakdown, setBreakdown] = useState([]);

  useEffect(() => {
    pub.get("/demo/dashboard?range=30d").then((r) => setSummary(r.data.summary || [])).catch(() => {});
    pub.get("/demo/series?metric=revenue&range=90d").then((r) => setRevenue(r.data || [])).catch(() => {});
    pub.get("/demo/series?metric=active_users&range=90d").then((r) => setUsers(r.data || [])).catch(() => {});
    pub.get("/demo/breakdown?metric=revenue&by=region&range=30d").then((r) => setBreakdown(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="demo-logo">
            <Activity className="h-5 w-5 text-primary" />
            <span className="font-heading text-lg font-semibold">PULSE</span>
            <span className="ml-2 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-primary">Live demo</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="demo-signin-button">Sign in</Button></Link>
            <Link to="/register"><Button size="sm" data-testid="demo-cta-header">Start free trial<ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold sm:text-4xl">SaaS metrics, at a glance</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            This is a live, read-only demo running on sample data. Connect your own CSV, Excel, Google Sheet, or API and get a dashboard like this in minutes.
          </p>
        </div>

        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="demo-kpi-grid">
          {summary.map((m) => <KpiCard key={m.key} m={m} />)}
        </section>

        <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-border bg-card/60 p-4 lg:col-span-2">
            <h3 className="mb-3 font-heading text-sm font-semibold">Revenue — last 90 days</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue} margin={MARGIN}>
                  <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={ACCENT} stopOpacity={0.35} /><stop offset="100%" stopColor={ACCENT} stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="date" tick={TICK} tickFormatter={(d) => d.slice(5)} minTickGap={40} />
                  <YAxis tick={TICK} width={44} tickFormatter={kfmt} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="value" stroke={ACCENT} strokeWidth={2} fill="url(#rev)" isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card/60 p-4">
            <h3 className="mb-3 font-heading text-sm font-semibold">Revenue by region</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={breakdown} margin={MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "hsl(240 5% 65%)" }} />
                  <YAxis tick={TICK} width={44} tickFormatter={kfmt} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(240 4% 16% / 0.4)" }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {breakdown.map((d, i) => <Cell key={d.name ?? i} fill={BARS[i % BARS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="mt-4 rounded-xl border border-border bg-card/60 p-4">
          <h3 className="mb-3 font-heading text-sm font-semibold">Active users — last 90 days</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={users} margin={MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="date" tick={TICK} tickFormatter={(d) => d.slice(5)} minTickGap={40} />
                <YAxis tick={TICK} width={44} tickFormatter={kfmt} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="value" stroke="hsl(142 71% 45%)" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="mt-10 rounded-2xl border border-primary/30 bg-primary/5 p-8 text-center">
          <h2 className="font-heading text-2xl font-bold">Build this with your own data</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            Import a spreadsheet or connect a live source and get real-time KPI dashboards, alerts, scheduled reports, and shareable links.
          </p>
          <div className="mt-5 flex justify-center gap-3">
            <Link to="/register"><Button size="lg" data-testid="demo-cta-footer">Start your free trial<ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
            <Link to="/"><Button size="lg" variant="outline" data-testid="demo-learn-more">Learn more</Button></Link>
          </div>
        </section>
      </main>
    </div>
  );
}
